import { useState, type FormEvent } from 'react';
import { Building2 } from 'lucide-react';
import { Button } from '@vd/ui';
import type { EnterpriseInquiryInput } from '@vd/types';
import { post, type ApiError } from '../../lib/apiClient';
import { BillingDialog } from './BillingDialog';
import styles from './Billing.module.css';

export function EnterpriseInquiryForm({
  companyId,
  companyName,
  onClose,
}: {
  companyId: string;
  companyName: string;
  onClose: () => void;
}) {
  const [requestId, setRequestId] = useState(() => crypto.randomUUID());
  const [busy, setBusy] = useState(false);
  const [queued, setQueued] = useState(false);
  const [error, setError] = useState('');
  const [fields, setFields] = useState({
    name: '',
    email: '',
    company: companyName,
    members: '',
    reports: '',
    needs: '',
  });
  const edit = (key: keyof typeof fields, value: string) => {
    setFields((old) => ({ ...old, [key]: value }));
    setRequestId(crypto.randomUUID());
    setError('');
  };
  const submit = async (event: FormEvent) => {
    event.preventDefault();
    if (busy || queued) return;
    setBusy(true);
    setError('');
    try {
      const answer = await post<{ status: string }>('/v1/billing/enterprise-inquiry', {
        companyId,
        requestId,
        ...fields,
      } satisfies EnterpriseInquiryInput);
      if (answer.status !== 'queued')
        throw new Error('We could not confirm your inquiry. Please retry.');
      setQueued(true);
    } catch (failure) {
      // Only a confirmed terminal job state permits a new identity. An ambiguous
      // transport/queue failure must keep the old identity for safe retry.
      if (['inquiry_delivery_failed', 'inquiry_changed'].includes((failure as ApiError).code ?? ''))
        setRequestId(crypto.randomUUID());
      setError((failure as Error).message);
    } finally {
      setBusy(false);
    }
  };
  return (
    <BillingDialog titleId="enterprise-title" onClose={onClose} dismissible={!busy}>
      <h2 id="enterprise-title" className={styles.sectionTitle}>
        <span className={`${styles.iconTile} ${styles.amber}`}>
          <Building2 aria-hidden="true" size={18} />
        </span>
        Talk to us about Enterprise
      </h2>
      <p className={styles.muted}>
        Tell us how your team plans to use Toado. Your inquiry goes to jason@toado.dev.
      </p>
      {queued ? (
        <div className={`${styles.notice} ${styles.success}`} role="status">
          Your inquiry is queued. We will reply to {fields.email}.
        </div>
      ) : (
        <form onSubmit={submit}>
          <fieldset disabled={busy} className={styles.form}>
            <legend className={styles.srOnly}>Enterprise inquiry</legend>
            <label>
              Your name (required)
              <input
                autoFocus
                autoComplete="name"
                required
                maxLength={100}
                value={fields.name}
                onChange={(e) => edit('name', e.target.value)}
              />
            </label>
            <label>
              Work email (required)
              <input
                type="email"
                autoComplete="email"
                required
                maxLength={254}
                value={fields.email}
                onChange={(e) => edit('email', e.target.value)}
              />
            </label>
            <label className={styles.wide}>
              Company or workspace (required)
              <input
                autoComplete="organization"
                required
                maxLength={150}
                value={fields.company}
                onChange={(e) => edit('company', e.target.value)}
              />
            </label>
            <label>
              Internal team members
              <select value={fields.members} onChange={(e) => edit('members', e.target.value)}>
                <option value="">Not sure yet</option>
                {['1-5', '6-20', '21-50', '51-100', '100+'].map((value) => (
                  <option key={value}>{value}</option>
                ))}
              </select>
            </label>
            <label>
              Expected widget reports per month
              <select value={fields.reports} onChange={(e) => edit('reports', e.target.value)}>
                <option value="">Not sure yet</option>
                {['Up to 5,000', '5,001-10,000', '10,001-50,000', 'More than 50,000'].map(
                  (value) => (
                    <option key={value}>{value}</option>
                  ),
                )}
              </select>
            </label>
            <label className={styles.wide}>
              What does your team need? (required)
              <textarea
                rows={4}
                required
                maxLength={2000}
                value={fields.needs}
                onChange={(e) => edit('needs', e.target.value)}
                placeholder="Your workflow, usage, integrations or other requirements"
              />
            </label>
          </fieldset>
          {error && (
            <p className={`${styles.notice} ${styles.error}`} role="alert">
              {error}
            </p>
          )}
          <div className={styles.dialogActions}>
            <Button type="button" variant="secondary" onClick={onClose} disabled={busy}>
              Cancel
            </Button>
            <Button type="submit" variant="primary" disabled={busy}>
              {busy ? 'Submitting…' : 'Send inquiry'}
            </Button>
          </div>
        </form>
      )}
      {queued && (
        <div className={styles.dialogActions}>
          <Button onClick={onClose}>Close</Button>
        </div>
      )}
    </BillingDialog>
  );
}
